// src/components/templates/InstitutionTemplate.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  School, 
  Users, 
  Calendar, 
  Bell, 
  FileText, 
  Image, 
  Phone, 
  Mail, 
  MessageCircle,
  MapPin,
  Award,
  BookOpen,
  User,
  Settings,
  ChevronDown,
  ExternalLink,
  Shield, 
  LogIn, 
  EyeOff,
  Download,
  Eye,
  Heart,
  Share2,
  Search,
  Menu,
  X,
  ChevronRight,
  Building2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { institutionService } from '../../services/institutionService';
import { ShareModal } from '../books/modals/ShareModal';

// Import all section components
import { InstitutionHeroSection } from '../institution/sections/InstitutionHeroSection';
import { AboutInstitutionSection } from '../institution/sections/AboutInstitutionSection';
import { LeadershipSection } from '../institution/sections/LeadershipSection';
import { AcademicProgramsSection } from '../institution/sections/AcademicProgramsSection';
import { EventsSection } from '../institution/sections/EventsSection';
import { NewsUpdatesSection } from '../institution/sections/NewsUpdatesSection';
import { ResourcesSection } from '../institution/sections/ResourcesSection';
import { GalleriesSection } from '../institution/sections/GalleriesSection';
import { AdmissionsSection } from '../institution/sections/AdmissionsSection';
import { ContactSection } from '../institution/sections/ContactSection';
import { PostsSection } from '../institution/sections/PostsSection';


// Import modal components
import { ContactModal } from '../institution/modals/ContactModal';
import { EventDetailsModal } from '../institution/modals/EventDetailsModal';
import { ResourceModal } from '../institution/modals/ResourceModal';
import { GalleryModal } from '../institution/modals/GalleryModal';
import { AdmissionsModal } from '../institution/modals/AdmissionsModal';
import { StaffDetailsModal } from '../institution/modals/StaffDetailsModal';

import { InstitutionManager } from '../institution/InstitutionManager';
import { SharedHeader } from '../shared/SharedHeader';

const SECTION_ICON_MAP = {
  hero: School,
  about: FileText,
  leadership: Users,
  programs: BookOpen,
  events: Calendar,
  posts: MessageCircle,
  resources: FileText,
  galleries: Image,
  facilities: Building2,
  admissions: User,
  contact: Phone
};

const SECTION_LABEL_MAP = {
  hero: 'Home',
  about: 'About',
  leadership: 'Leadership',
  programs: 'Programs',
  events: 'Events',
  posts: 'Posts',
  resources: 'Resources',
  galleries: 'Gallery',
  facilities: 'Facilities',
  admissions: 'Admissions',
  contact: 'Contact'
};

// Loading component
const InstitutionLoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading institution...</p>
    </div>
  </div>
);

export const InstitutionTemplate = ({ 
  bookId, 
  book, 
  bookData,
  viewManager,
  styling = {},
  customStyling = {},
  customCSS = ''
}) => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('hero');
  const [institutionData, setInstitutionData] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Modal states
  
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedPosts, setSelectedPosts] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAdmissionsModal, setShowAdmissionsModal] = useState(false);

  // Data states for different sections
  const [programs, setPrograms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [resources, setResources] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [stats, setStats] = useState({});

  const { viewMode, isAuthenticated } = viewManager || { viewMode: 'public', isAuthenticated: false };
  const [isOwner, setIsOwner] = useState(false);

  const effectiveBookData = bookData || book;
  const institutionSettings = effectiveBookData?.institutionSettings || {};

  

  // Merge styling
  const mergedStyling = {
    // Document data takes priority
    primaryColor: effectiveBookData?.primaryColor || '#1e40af',
    backgroundColor: styling?.backgroundColor || customStyling?.backgroundColor || effectiveBookData?.styling?.backgroundColor || effectiveBookData?.backgroundColor || '#ffffff',
    headerColor: styling?.headerColor || customStyling?.headerColor || effectiveBookData?.styling?.headerColor || effectiveBookData?.headerColor || '#1e40af',
    headerTextColor: styling?.headerTextColor || customStyling?.headerTextColor || effectiveBookData?.styling?.headerTextColor || effectiveBookData?.headerTextColor || '#ffffff',
    accentColor: styling?.accentColor || customStyling?.accentColor || effectiveBookData?.styling?.accentColor || effectiveBookData?.accentColor || '#3b82f6',
    fontFamily: styling?.fontFamily || customStyling?.fontFamily || effectiveBookData?.styling?.fontFamily || effectiveBookData?.fontFamily || 'Inter, system-ui, sans-serif',
    
    // Static properties
    secondaryColor: '#3b82f6',
    textColor: '#374151',
    borderRadius: '8px',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  };

  // Determine visible sections based on settings
  const sectionVisibility = institutionSettings?.sectionVisibility || {};
  const visibleSections = Object.keys(SECTION_LABEL_MAP).filter(sectionId => {
    const visibility = institutionSettings?.sectionVisibility?.[sectionId];
    return visibility !== false;
  });

  useEffect(() => {
    loadInstitutionData();
  }, [bookId]);

  useEffect(() => {
    // Only run scroll handler in public view
    if (viewMode !== 'public') return;

    // Update active section when scrolling
    const handleScroll = () => {
      const sections = visibleSections.map(sectionId => {
        const element = document.getElementById(`section-${sectionId}`);
        return {
          id: sectionId,
          element,
          top: element ? element.offsetTop - 100 : 0
        };
      });

      // Filter out sections without elements
      const validSections = sections.filter(section => section.element);

      // Guard against empty array
      if (validSections.length === 0) return;

      // Use reduce with initial value to prevent error
      const currentSection = validSections.reduce((prev, current) => {
        return (window.scrollY >= current.top && window.scrollY >= prev.top) ? current : prev;
      }, validSections[0]); // Provide first section as initial value

      if (currentSection && currentSection.id !== activeSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleSections, activeSection, viewMode]);

  const loadInstitutionData = async () => {
    try {
      setLoading(true);
      
      if (bookId) {
        console.log('Loading institution data for bookId:', bookId);
        
        // Use the new comprehensive method
        const completeData = await institutionService.getCompleteInstitutionData(bookId);
        
        setInstitutionData(completeData);
        
        // Set individual data arrays
        setPrograms(completeData.programs || []);
        setStaff(completeData.staff || []);
        setEvents(completeData.events || []);
        setPosts(completeData.posts || []);
        setResources(completeData.resources || []);
        setFacilities(completeData.facilities || []);
        setGalleries(completeData.galleries || []);
        setStats(completeData.stats || {});
        
      }
    } catch (error) {
      console.error('Error loading institution data:', error);
      // Set default empty data to prevent rendering issues
      setInstitutionData(effectiveBookData || {});
      setPrograms([]);
      setStaff([]);
      setEvents([]);
      setPosts([]);
      setResources([]);
      setFacilities([]);
      setGalleries([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  };


  
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    
    // Scroll to section
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementTop = element.offsetTop - offset;
      window.scrollTo({
        top: elementTop,
        behavior: 'smooth'
      });
    }
  };

  const handleUpdateGallery = async (galleryId, updatedData) => {
    try {
      await institutionService.updateGallery(bookId, galleryId, updatedData);
      // Reload galleries data
      await loadInstitutionData();
    } catch (error) {
      console.error('Error updating gallery:', error);
      throw error;
    }
  };

  const handleCreatePost = async (postData) => {
    try {
      await institutionService.createPost(bookId, postData);
      await loadInstitutionData(); // Reload posts
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  };

  const handleUpdatePost = async (postId, postData) => {
    try {
      await institutionService.updatePost(postId, postData);
      await loadInstitutionData();
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await institutionService.deletePost(postId);
        await loadInstitutionData();
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Failed to delete post');
      }
    }
  };

  const handleModeratePost = async (postId, status) => {
    try {
      await institutionService.moderatePost(postId, status);
      await loadInstitutionData();
    } catch (error) {
      console.error('Error moderating post:', error);
      alert('Failed to moderate post');
    }
  };

  // Event handlers for modals
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
  };

  const handleGalleryClick = (gallery) => {
    setSelectedGallery(gallery);
  };

  const handlePostClick = (postItem) => {
    setSelectedPosts(postItem);
  };

  const handleProgramClick = (program) => {
    setSelectedProgram(program);
  };

  const handleStaffClick = (staffMember) => {
    setSelectedStaff(staffMember);
  };

  
  const getSectionComponent = (sectionId) => {
    const commonProps = {
      bookId,
      data: effectiveBookData,
      institutionData,
      settings: institutionSettings,
      styling: mergedStyling
    };

    try {
      switch (sectionId) {
        case 'hero':
          return (
            <InstitutionHeroSection
              {...commonProps}
              stats={stats}
              onSectionChange={handleSectionChange}
            />
          );
        case 'about':
          return <AboutInstitutionSection {...commonProps} />;
        case 'leadership':
          return (
            <LeadershipSection 
              {...commonProps} 
              staffMembers={staff} 
              onPersonClick={handleStaffClick}
            />
          );
        case 'programs':
          return (
            <AcademicProgramsSection
              {...commonProps}
              programs={programs}
              onProgramClick={handleProgramClick}
            />
          );
        case 'events':
          return (
            <EventsSection
              {...commonProps}
              events={events}
              onEventClick={handleEventClick}
            />
          );
        case 'posts':
          return (
            <PostsSection
              {...commonProps}
              posts={posts} 
              user={user}
              isModerator={effectiveBookData?.moderators?.includes(user?.uid) || effectiveBookData?.ownerId === user?.uid}
              onCreatePost={handleCreatePost}
              onUpdatePost={handleUpdatePost}
              onDeletePost={handleDeletePost}
              onModeratePost={handleModeratePost}
            />
          );
        case 'resources':
          return (
            <ResourcesSection
              {...commonProps}
              resources={resources}
              onResourceClick={handleResourceClick}
            />
          );
        case 'galleries':
          return (
            <GalleriesSection
              {...commonProps}
              galleries={galleries}
              onGalleryClick={handleGalleryClick}
              isOwner={effectiveBookData?.ownerId === user?.uid} 
              onUpdateGallery={handleUpdateGallery} 
            />
          );
        case 'facilities':
          return (
            <div className="py-20 px-4 text-center">
              <h2 className="text-3xl font-bold mb-4">Facilities</h2>
              <p className="text-gray-600">Facilities section coming soon...</p>
            </div>
          );
        case 'admissions':
          return (
            <AdmissionsSection
              {...commonProps}
              onAdmissionsClick={() => setShowAdmissionsModal(true)}
            />
          );
        case 'contact':
          return (
            <ContactSection
              {...commonProps}
              onContactClick={() => setShowContactModal(true)}
            />
          );
        default:
          return (
            <div className="py-20 px-4 text-center">
              <h2 className="text-3xl font-bold mb-4">Section Coming Soon</h2>
              <p className="text-gray-600">This section is under development.</p>
            </div>
          );
      }
    } catch (error) {
      console.error(`Error rendering section ${sectionId}:`, error);
      return (
        <div className="py-20 px-4 text-center">
          <h2 className="text-3xl font-bold text-red-600 mb-4">Error Loading Section</h2>
          <p className="text-gray-600">There was an error loading this section.</p>
        </div>
      );
    }
  };

  if (loading) {
    return <InstitutionLoadingSpinner />;
  }

  // Show management view if authenticated and in management mode
  if (viewMode === 'management' && isAuthenticated) {
    return (
      <>
        <InstitutionManager
          user={user}
          bookId={bookId}
          bookData={bookData}
          institutionData={institutionData}
          onUpdate={loadInstitutionData}
          
          customStyling={mergedStyling}
        />
        
      </>
    );
  }

  return (
    <div 
      className="min-h-screen institution-template"
      style={{
        backgroundColor: mergedStyling.backgroundColor,
        fontFamily: mergedStyling.fontFamily,
        color: mergedStyling.textColor
      }}
    >
      {/* Custom CSS */}
      {customCSS && (
        <style dangerouslySetInnerHTML={{ __html: customCSS }} />
      )}

      {/* Navigation Header */}
      <SharedHeader
        templateType="institution"
        bookData={effectiveBookData}
        styling={mergedStyling}
        isOwner={false} // Set based on your auth logic
        navigate={window.navigate || ((path) => window.location.href = path)}
        institutionSections={visibleSections}
        activeInstitutionSection={activeSection}
        onInstitutionSectionChange={handleSectionChange}
        enableShareTracking={true}
      />

      {/* Main Content */}
      <main className="flex-1">
        {visibleSections.map((sectionId) => (
          <div 
            key={sectionId}
            id={`section-${sectionId}`}
            className="section-scroll-target scroll-mt-10"
          >
            {getSectionComponent(sectionId)}
          </div>
        ))}
      </main>

      {/* Footer */}
      <footer 
        className="py-8 border-t"
        style={{ 
          backgroundColor: `${mergedStyling.headerColor}10`,
          borderColor: `${mergedStyling.headerColor}20`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              {effectiveBookData?.logo && (
                <img 
                  src={effectiveBookData.logo} 
                  alt="Logo" 
                  className="h-8 w-8 rounded-full object-cover"
                />
              )}
              <div>
                <p className="font-semibold" style={{ color: mergedStyling.textColor }}>
                  {effectiveBookData?.botName || 'Institution'}
                </p>
                <p className="text-sm text-gray-500">
                  {institutionSettings.tagline || 'Excellence in Education'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>© 2024 {effectiveBookData?.botName || 'Institution'}</span>
              
              <button
                onClick={() => setShowContactModal(true)}
                className="hover:text-gray-700 transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>

      {showContactModal && (
        <ContactModal
          institution={effectiveBookData}
          onClose={() => setShowContactModal(false)}
        />
      )}

      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {selectedResource && (
        <ResourceModal
          resource={selectedResource}
          onClose={() => setSelectedResource(null)}
        />
      )}

      {selectedGallery && (
        <GalleryModal
          gallery={selectedGallery}
          onClose={() => setSelectedGallery(null)}
        />
      )}

      {showAdmissionsModal && (
        <AdmissionsModal
          programs={programs}
          institution={effectiveBookData}
          onClose={() => setShowAdmissionsModal(false)}
          onSubmit={async (applicationData) => {
            try {
              await institutionService.createAdmission(bookId, applicationData);
              setShowAdmissionsModal(false);
              alert('Application submitted successfully!');
            } catch (error) {
              console.error('Error submitting application:', error);
              alert('Failed to submit application. Please try again.');
            }
          }}
        />
      )}

      {selectedStaff && (
        <StaffDetailsModal
          staff={selectedStaff}
          onClose={() => setSelectedStaff(null)}
          styling={mergedStyling}
        />
      )}

      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 left-6 z-40 p-3 rounded-full shadow-lg transition-all hover:shadow-xl"
        style={{ 
          backgroundColor: mergedStyling.primaryColor,
          color: 'white'
        }}
        title="Scroll to top"
      >
        <ChevronRight className="h-5 w-5 rotate-[-90deg]" />
      </button>
    </div>
  );
};